import { localeFor } from "../lib/languages";
import { ui } from "../lib/localization";
import { Clock3, Download, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { translations, type Language } from "../i18n";
import { formatElapsedDuration } from "../lib/time";
import type { GeneratedFile } from "../types";
import { fileAvailability, isEmbeddedResourceUrl } from "../lib/resource-cache";
import { FileSaveDialog } from "./FileSaveDialog";

interface GeneratedFileGalleryProps {
  files?: GeneratedFile[];
  language: Language;
}

const formatFileSize = (bytes: number, language: Language): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString(localeFor(language), { maximumFractionDigits: 1 })} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString(localeFor(language), { maximumFractionDigits: 1 })} MB`;
};

export function GeneratedFileGallery({ files = [], language }: GeneratedFileGalleryProps) {
  const t = translations[language];
  const [now, setNow] = useState(Date.now);
  const [selected, setSelected] = useState<GeneratedFile>();
  useEffect(() => {
    if (!files.some((file) => file.expiresAt)) return;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [files]);
  if (!files.length) return null;

  return (
    <div className="generated-file-list">
      {files.map((file) => {
        const local = isEmbeddedResourceUrl(file.downloadUrl);
        const availability = fileAvailability(local ? undefined : file.expiresAt, language, now);
        const label = !local && !file.expiresAt ? ui(language, "Temporary download link") : availability.label;
        return (
        <article className="generated-file" key={file.id}>
          <span className="generated-file-icon"><FileText size={20} /></span>
          <span className="generated-file-copy">
            <strong>{file.fileName}</strong>
            <small>
              {file.format.toUpperCase()} · {formatFileSize(file.size, language)}
              {file.elapsedMs != null ? ` · ${t.fileGenerationDuration(formatElapsedDuration(file.elapsedMs, language))}` : ""}
            </small>
            <em><Clock3 size={12} />{label}</em>
          </span>
          <button
            type="button"
            className="generated-file-download"
            disabled={availability.expired && !local}
            onClick={() => setSelected(file)}
            aria-label={t.downloadFile(file.fileName)}
            title={t.downloadFile(file.fileName)}
          ><Download size={17} /></button>
        </article>
      ); })}
      {selected && <FileSaveDialog file={selected} language={language} onClose={() => setSelected(undefined)} />}
    </div>
  );
}
